import { useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import useStore, { storeState } from '~/store';

/**
 * Hook customizado para carregar dados do contrato quando necessário
 * Este hook verifica se o contract_id existe na store, e se não existir
 * mas houver um cid nos searchParams, ele carrega os dados do contrato
 *
 * @param options - Opções de configuração
 * @param options.skipEmailMode - Se true, pula o carregamento quando isEmailMode for true
 * @param options.stepName - Nome do step para logs (opcional)
 */
interface UseContractLoaderOptions {
  skipEmailMode?: boolean;
  stepName?: string;
}

export const useContractLoader = (options: UseContractLoaderOptions = {}) => {
  const { skipEmailMode = false, stepName = 'unknown' } = options;
  const [searchParams] = useSearchParams();

  const { contractAsyncGetContractByHashId, contractAsyncGetContext, setCameFromEmail, contract_id } = useStore(
    (store) => ({
      contractAsyncGetContractByHashId: store.contractAsyncGetContractByHashId,
      contractAsyncGetContext: store.contractAsyncGetContext,
      setCameFromEmail: store.setCameFromEmail,
      contract_id: store.contract_id
    })
  );

  // Carregar dados do contrato se não existirem
  useEffect(() => {
    const fetchContractData = async () => {
      const contract_hash_id = searchParams.get('cid');
      const source = searchParams.get('source');
      const intMod = searchParams.get('int_mod');
      const isEmailMode = source === 'email' && intMod === '1' && !!contract_hash_id;

      // Se skipEmailMode estiver ativo e estivermos no modo email, não fazer nada
      if (skipEmailMode && isEmailMode) {
        return;
      }

      if (!contract_id && contract_hash_id) {
        try {
          const orgParam = searchParams.get('org');
          await contractAsyncGetContractByHashId(contract_hash_id, orgParam ?? undefined);

          // Marcar como "veio do email" quando source=email (com ou sem int_mod, ex.: contract/user?cid=xxx&source=email&org=xxx)
          if (source === 'email') {
            setCameFromEmail(true);
          }
        } catch (error) {
          console.error(`Erro ao carregar dados do contrato no ${stepName}:`, error);
          storeState.addToast({
            title: 'Erro',
            message: 'Não foi possível carregar os dados do contrato.',
            type: 'error'
          });
        }
      }
    };

    fetchContractData();
  }, [contract_id, searchParams, contractAsyncGetContractByHashId, setCameFromEmail, skipEmailMode, stepName]);

  useEffect(() => {
    if (contract_id) {
      const orgParam = searchParams.get('org');
      const fetchContext = async () => {
        try {
          await contractAsyncGetContext(contract_id, orgParam ?? undefined);
        } catch (error) {
          console.error(`Erro ao buscar contexto no ${stepName}:`, error);
          storeState.addToast({
            title: 'Erro',
            message: 'Não foi possível carregar o contexto do contrato.',
            type: 'error'
          });
        }
      };
      fetchContext();
    }
  }, [contract_id, searchParams, contractAsyncGetContext, stepName]);

  return {
    contract_id,
    isLoading: !contract_id && !!searchParams.get('cid')
  };
};
